import type { Multirange, Range } from '@attain-apps/range'
import type { PartialId } from '@attain-apps/string-builder'
import { TypeChecker } from '@attain-apps/type-guards'

import type * as models from '../../../../models'
import * as expression from '../../../expression'
import type { Mutation } from '../../../mutation'
import type { Query } from '../../query'
import type { Target } from '../../target'
import * as utils from '../utils'

import type { Context } from './context'
import { Node } from './node'

/**
 * TODO: update documentation
 *
 * {@link Query} is designed to be flexible and easy to create. However, that
 * flexibility makes it hard to consume in a consistent way without a bunch of
 * logic (e.g. set default values for missing properties). This type should be
 * used when processing a {@link Query}
 *
 * Use {@link Query.standardize} to convert a {@link Query} to a
 * {@link Query.Standardized}
 */
export class ForRecords<out T = unknown> extends Node {
  /**
   * Results are based on records of this model. If a dimension id is given,
   * then all entities in that dimension are used, unless this is a subquery and
   * the dimension ID is for the dimension containing the parent query's entity,
   * in which case only the ancestors of the parent query's entity are used
   */

  /** @see {@link Source}  */
  public readonly source: ForRecords.Source

  /**
   * @see {@link Query.selection}
   */
  public readonly selection: expression.node.Node<T>
  /**
   * @see {@link Query.assumption}
   */
  public readonly assumption: readonly Mutation[]
  /**
   * @see {@link Query.sort}
   */
  public readonly sort?: {
    readonly by: expression.fn.node.Node<number>
    readonly descending: boolean
  }
  /**
   * Corresponds to the portion of the {@link Query.filter} that only
   * pertains to this node's model
   *
   * __NOTE:__ this can be used to filter out records of this node's model
   * without requiring the results of any other node
   */
  public readonly filterUsingOnlySelf?: expression.fn.node.Node<boolean>
  /**
   * Corresponds to the portion of the {@link Query.filter} that at least
   * partially pertains to consumed nodes
   *
   * __NOTE:__ this can _only_ be used to filter out results _after_
   * getting the results from all nodes that are consumed by this node
   */
  public readonly filterUsingConsumingNodes?: expression.fn.node.Node<boolean>
  /**
   * An {@link expression.node.Node} that produces a {@link Multirange} or
   * {@link Range} of {@link Date} in which the produced records must be active
   *
   * If this is `undefined`, the results are not filtered by active period
   *
   * @see {@link Query.period}
   */
  public readonly period?: expression.node.Node<Multirange<Date> | Range<Date>>
  /**
   * @see {@link Query.purpose}
   */
  public readonly purpose: {
    readonly include: readonly PartialId[] | 'all'
    readonly nil: NonNullable<NonNullable<Query['purpose']>['nil']>
  }
  /**
   * @see {@link Query.quantity}
   */
  public readonly quantity?: number
  /**
   * @see {@link Query.skip}
   */
  public readonly skip?: number

  constructor(params: ForRecords.constructor.Params<T>) {
    super(params)
    this.assumption = params.assumption
    this.source = params.source
    this.period = params.period
    this.purpose = params.purpose
    this.quantity = params.quantity
    this.selection = params.selection
    this.skip = params.skip
    this.sort = params.sort

    if (params.filter) {
      let conjunctiveFilters: readonly expression.fn.node.Node<boolean>[]
      if (expression.fn.definitions.And.Node.isNode(params.filter)) {
        conjunctiveFilters = params.filter.arg
      } else if (
        expression.fn.definitions.Not.Node.isNode(params.filter) &&
        expression.fn.definitions.Or.Node.isNode(params.filter.arg)
      ) {
        // https://en.wikipedia.org/wiki/De_Morgan%27s_laws#:~:text=The%20negation%20of%20a%20disjunction%20is%20the%20conjunction%20of%20the%20negations
        conjunctiveFilters = params.filter.arg.arg.map(
          arg => new expression.fn.definitions.Not.Node(arg),
        )
      } else {
        conjunctiveFilters = [params.filter]
      }
      const selfContainedFilters: expression.fn.node.Node<boolean>[] = []
      const consumingNodesFilters: expression.fn.node.Node<boolean>[] = []
      for (const filter of conjunctiveFilters) {
        // TODO: if filter contains a GetSubquery node, then it belongs in
        // consumingNodesFilters. Otherwise, it belongs in
        // selfContainedFilters
        consumingNodesFilters.push(filter)
      }

      // If only one of the filter properties is going to be defined, it
      // should use the original params.filter, as to avoid any
      // modifications we made when splitting the conjunctiveFilters above

      // If there are no self-contained filters, then this can use
      // params.filter
      this.filterUsingConsumingNodes =
        selfContainedFilters.length === 0
          ? params.filter
          : consumingNodesFilters.length >= 2
            ? new expression.fn.definitions.And.Node(selfContainedFilters)
            : selfContainedFilters[0]
      // Only if consumingNodesFilter is not defined, can we use
      // params.filter here. This is not the same check as above because if
      // both lists somehow end up empty, we don't want to put params.filter
      // in both places, and it is safer if it ends up on
      // consumingNodesFilter
      this.filterUsingOnlySelf = !this.filterUsingConsumingNodes
        ? params.filter
        : selfContainedFilters.length >= 2
          ? new expression.fn.definitions.And.Node(selfContainedFilters)
          : selfContainedFilters[0]
    }

    // NOTE: this (creating connection nodes) MUST happen before the
    // consuming edges are added in order for
    // _determineIfResultsShouldBeSingleton to work as expected
    params.subqueryNodes.bind(this)

    utils.SubqueryNodeLookup.consume(this, params.subqueryNodes.nodes, params)
  }
}
export namespace ForRecords {
  export const isForRecords: TypeChecker<ForRecords> =
    TypeChecker.Factory.instance(ForRecords).usingAlias(
      'query.graph.node.ForEntity',
    )

  export namespace constructor {
    export type Params<T = unknown> = Omit<
      Difference<ForRecords<T>, Node>,
      `${string}Filter`
    > &
      Pick<Context, 'schema'> &
      Node.constructor.Params & {
        /**
         * This is split into {@link ForRecords.filterUsingOnlySelf} and
         * {@link ForRecords.filterUsingConsumingNodes}
         */
        filter?: expression.fn.node.Node<boolean>
        subqueryNodes: BindableSubqueryNodes
      }
  }

  export interface BindableSubqueryNodes {
    nodes: utils.SubqueryNodeLookup
    /**
     * Callback to pass 'parent' node usually used to create connections
     * between nodes.
     */
    bind: (node: ForRecords) => void
  }

  /**
   * Source can be either {@link models.ForRecord.Id} (e.g. 'ent:!space:!room') or
   * {@link Target.DateRange.List}
   */
  export type Source = ExactlyOneOf<{
    readonly modelId: models.ForRecord.Id
    // TODO: Should support list of any values.
    readonly dateRangeList: Target.DateRange.List
  }>
}
